import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  db,
  kvGet,
  payOrder,
  saveEInvoice,
  servicePercentText,
  syncServiceFee,
  debtOf,
  setOrderCustomer,
  type Customer,
  type OrderEInvoice,
  type PayMethod,
} from '../lib/db'
import { defaultPrinter, printReceipt, probeAgent, type PrinterConfig } from '../lib/agent'
import { einvoiceApi, invoiceLabel, type EInvoiceBuyer } from '../lib/einvoice'
import { cashSuggestions, money, qtyToText } from '../lib/money'
import { useApp } from '../lib/store'
import { pushNow } from '../lib/sync'
import { AppBar } from '../components/AppBar'
import { BuyerSheet } from '../components/BuyerSheet'
import { CustomerPicker } from '../components/CustomerPicker'
import { DiscountSheet } from '../components/DiscountSheet'
import { NumPad } from '../components/NumPad'
import { VietQr } from '../components/VietQr'

/**
 * Hạn chờ hoá đơn điện tử, tính từ lúc bấm thu tiền.
 *
 * Quá mốc này thì thôi, in bill mà không có mã cơ quan thuế: hàng đợi trên máy chủ vẫn
 * xuất tiếp, khách cần tờ có mã thì in lại sau. Người bán không được đứng chờ mạng.
 */
const INVOICE_WAIT_MS = 9000

export function PayScreen() {
  const { orderId = '' } = useParams()
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)

  // Màn hình bán nhanh đã hỏi khách trả bằng gì rồi thì đừng hỏi lại ở đây.
  const [params] = useSearchParams()
  const [method, setMethod] = useState<PayMethod>(
    params.get('m') === 'Transfer' ? 'Transfer' : 'Cash',
  )
  const [given, setGiven] = useState(0)
  const [done, setDone] = useState<{ total: number; change: number } | null>(null)
  const [printer, setPrinter] = useState<PrinterConfig | null>(null)
  const [printing, setPrinting] = useState(false)
  const [discounting, setDiscounting] = useState(false)

  /*
   * Ghi sổ: đơn đóng lại nhưng tiền chưa vào két.
   *
   * Phải chọn đúng một khách quen, không cho gõ tên tự do — tên gõ tay thì "anh Tuấn" hôm
   * nay và "a Tuan" hôm sau là hai người khác nhau, và cuối tháng không ai cộng ra được số
   * nợ thật. Chọn từ danh sách thì mỗi khoản nợ có đúng một chủ.
   */
  const [picking, setPicking] = useState(false)
  const [debtor, setDebtor] = useState<Customer | null>(null)
  const debtorOwes = useLiveQuery(
    async () => (debtor ? await debtOf(debtor.id) : 0),
    [debtor?.id],
    0,
  )

  // ── Hoá đơn điện tử ──
  const [buyer, setBuyer] = useState<EInvoiceBuyer | null>(null)
  const [askBuyer, setAskBuyer] = useState(false)
  const [invoice, setInvoice] = useState<OrderEInvoice | null>(null)
  const [issuing, setIssuing] = useState(false)
  const eInvoiceOn = shop?.eInvoiceEnabled ?? false

  // Hỏi trước xem máy này có in được không, để nút In bill chỉ hiện khi bấm vào là có
  // giấy chạy ra. Nút bấm không làm gì cả còn tệ hơn là không có nút.
  useEffect(() => {
    void (async () => {
      const saved = await kvGet<PrinterConfig>('printer')
      if (!saved?.address) return
      if (await probeAgent()) setPrinter(saved ?? defaultPrinter)
    })()
  }, [])

  // Chốt lại mức phí phục vụ ngay trước khi thu tiền: đây là con số khách sắp trả.
  useEffect(() => {
    void syncServiceFee(orderId)
  }, [orderId])

  const order = useLiveQuery(() => db.orders.get(orderId), [orderId])
  if (!order) return <div className="screen" />

  const total = order.total
  const fee = order.serviceFee ?? 0
  const change = given - total
  const canPay =
    method === 'Transfer' || (method === 'Debt' ? debtor !== null : given >= total)

  /** Ghi thêm đơn này vào là khách vượt trần nợ chủ quán đặt cho họ. */
  const overLimit =
    debtor !== null && debtor.debtLimit > 0 && debtorOwes + total > debtor.debtLimit

  /**
   * Xin phát hành hoá đơn điện tử.
   *
   * Có hạn chờ, và hạn ấy không phải để làm đẹp: nhà cung cấp treo thì hàng người đợi trả
   * tiền cũng treo theo. Hết hạn thì bỏ qua, máy chủ vẫn xuất tiếp trong hàng đợi.
   */
  async function settleInvoice(withBuyer: EInvoiceBuyer | null) {
    if (!eInvoiceOn) return

    setIssuing(true)
    try {
      const work = (async () => {
        // Đẩy đơn lên trước: máy chủ chưa có đơn thì chưa xuất hoá đơn cho nó được.
        await pushNow()
        return await einvoiceApi.issue(orderId, withBuyer ?? undefined)
      })()

      const res = await Promise.race([
        work,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), INVOICE_WAIT_MS)),
      ])

      if (res) {
        const snapshot: OrderEInvoice = {
          status: res.status,
          templateCode: res.templateCode,
          series: res.series,
          invoiceNo: res.invoiceNo,
          taxAuthorityCode: res.taxAuthorityCode,
          lookupUrl: res.lookupUrl,
          issuedAt: res.issuedAt,
          error: res.error,
        }
        await saveEInvoice(orderId, snapshot)
        setInvoice(snapshot)
        if (res.status === 'Failed' && res.error) say(res.error)
      }
    } catch (e) {
      // Mất mạng là chuyện thường ở quán. Không báo đỏ, hàng đợi trên máy chủ lo tiếp.
      if (e instanceof Error && e.message !== 'Không có mạng') say(e.message)
    }
    setIssuing(false)
  }

  async function pay() {
    if (!order || !canPay) return

    // Gắn khách vào đơn TRƯỚC khi đóng: đóng xong rồi mới gắn thì có một khoảnh khắc đơn
    // đã ghi sổ mà không biết ghi cho ai, và mất điện đúng lúc ấy là mất luôn khoản nợ.
    if (method === 'Debt' && debtor) await setOrderCustomer(order.id, debtor.id)

    // Ghi sổ thì khách chưa đưa đồng nào, nên số tiền nhận là 0 chứ không phải tổng đơn.
    await payOrder(order.id, method, method === 'Cash' ? given : method === 'Debt' ? 0 : total)
    setDone({ total: order.total, change: method === 'Cash' ? Math.max(0, given - order.total) : 0 })

    // Tự xuất mọi đơn, hoặc khách đã khai thông tin lấy hoá đơn.
    if (eInvoiceOn && (shop?.eInvoiceAutoIssue || buyer)) void settleInvoice(buyer)
    else void pushNow()
  }

  if (done) {
    const label = invoice && invoice.status === 'Issued' ? invoiceLabel(invoice) : null

    return (
      <div className="screen">
        {askBuyer && (
          <BuyerSheet
            value={buyer}
            onClose={() => setAskBuyer(false)}
            onSave={(b) => {
              setBuyer(b)
              setAskBuyer(false)
              void settleInvoice(b)
            }}
          />
        )}

        <div className="body center">
          <div className="done">
            <div className="done-mark" aria-hidden="true">
              ✓
            </div>
            <h2>Đã thu {money(done.total)}đ</h2>
            {done.change > 0 ? (
              <p className="done-change">
                Thối lại khách <b className="num">{money(done.change)}đ</b>
              </p>
            ) : (
              <p>
                {method === 'Cash'
                  ? 'Khách đưa đúng tiền'
                  : method === 'Transfer'
                    ? 'Đã nhận chuyển khoản'
                    : `Đã ghi sổ cho ${debtor?.name ?? 'khách quen'}`}
              </p>
            )}

            {eInvoiceOn && (
              <p className="note">
                {issuing
                  ? 'Đang xin mã cơ quan thuế…'
                  : label
                    ? `Hoá đơn ${label}`
                    : invoice?.status === 'Failed'
                      ? (invoice.error ?? 'Chưa xuất được hoá đơn')
                      : invoice || shop?.eInvoiceAutoIssue
                        ? 'Hoá đơn sẽ xuất xong khi máy có mạng'
                        : 'Chưa xuất hoá đơn cho đơn này'}
              </p>
            )}
          </div>
        </div>

        <div className="dock">
          {eInvoiceOn && !invoice && !issuing && (
            <button type="button" className="btn btn-ghost" onClick={() => setAskBuyer(true)}>
              🧾 Xuất hoá đơn
            </button>
          )}

          {printer && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={printing || issuing}
              onClick={async () => {
                setPrinting(true)
                try {
                  const fresh = await db.orders.get(orderId)
                  if (fresh) await printReceipt(fresh, shop, printer)
                } catch (e) {
                  say(e instanceof Error ? e.message : 'Không in được')
                }
                setPrinting(false)
              }}
            >
              {printing ? 'Đang in…' : issuing ? 'Đợi mã hoá đơn…' : 'In bill'}
            </button>
          )}
          <button type="button" className="btn btn-brand grow" onClick={() => nav('/tables')}>
            Xong
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="screen">
      {askBuyer && (
        <BuyerSheet
          value={buyer}
          onClose={() => setAskBuyer(false)}
          onSave={(b) => {
            setBuyer(b)
            setAskBuyer(false)
          }}
        />
      )}

      {discounting && <DiscountSheet order={order} onClose={() => setDiscounting(false)} />}

      {picking && (
        <CustomerPicker
          onClose={() => setPicking(false)}
          onPick={(c) => {
            setDebtor(c)
            setPicking(false)
          }}
        />
      )}

      <AppBar
        title="Thu tiền"
        sub={`${order.tableName ?? 'Bán nhanh'} · ${qtyToText(order.items.reduce((s, i) => s + i.qtyMilli, 0))} món`}
        onBack={() => nav(`/order/${order.id}`)}
      />

      {/*
        Hai khoang trên màn hình rộng: bên trái là con số và mấy lựa chọn, bên phải là
        bàn phím. Trước đây tất cả xếp thành một cột dài rồi tràn xuống dưới mép màn
        hình — người bán phải cuộn để thấy hàng phím cuối và nút Xong, giữa lúc khách
        đang đứng chìa tiền. Màn hình hẹp thì vẫn là một cột như cũ.
      */}
      <div className="body pay-body">
        <div className="pay-split">
        <div className="pay-side">
        <div className="pay-total">
          {/*
            Chỉ hiện tạm tính khi tổng tiền khác tiền hàng. Hoá đơn không bớt cũng không
            thu phí thì hai dòng bằng nhau, mà hai con số giống nhau xếp cạnh nhau là mời
            người bán đọc nhầm.
          */}
          {(order.discount > 0 || fee > 0) && (
            <>
              <div className="pay-row">
                <span>Tạm tính</span>
                <b className="num">{money(order.subtotal)}</b>
              </div>
              {order.discount > 0 && (
                <div className="pay-row">
                  <span>{order.discountLabel ?? 'Giảm giá'}</span>
                  <b className="num">−{money(order.discount)}</b>
                </div>
              )}
              {fee > 0 && (
                <div className="pay-row">
                  <span>Phí phục vụ ({servicePercentText(order.serviceFeePermille)}%)</span>
                  <b className="num">{money(fee)}</b>
                </div>
              )}
            </>
          )}

          <div className="pay-row big">
            <span>Khách trả</span>
            <b className="num">{money(total)}</b>
          </div>

          {method === 'Cash' && (
            <>
              <div className="pay-row">
                <span>Khách đưa</span>
                <b className="num">{given ? money(given) : '—'}</b>
              </div>
              <div className={`pay-row change${change < 0 ? ' short' : ''}`}>
                <span>{change < 0 ? 'Còn thiếu' : 'Tiền thối'}</span>
                <b className="num">{given ? money(Math.abs(change)) : '—'}</b>
              </div>
            </>
          )}
        </div>

        <button type="button" className="row-btn" onClick={() => setDiscounting(true)}>
          <span className="row-icon" aria-hidden="true">
            🎁
          </span>
          <span className="row-text">
            <b>{order.discount > 0 ? (order.discountLabel ?? 'Giảm giá') : 'Giảm giá cho khách?'}</b>
            <small>
              {order.discount > 0
                ? `Đã bớt ${money(order.discount)}đ`
                : 'Chương trình của quán, hoặc bớt thẳng một số tiền'}
            </small>
          </span>
          <span aria-hidden="true">›</span>
        </button>

        <div className="seg seg-lg seg-3">
          <button type="button" aria-pressed={method === 'Cash'} onClick={() => setMethod('Cash')}>
            💵 Tiền mặt
          </button>
          <button type="button" aria-pressed={method === 'Transfer'} onClick={() => setMethod('Transfer')}>
            📱 Chuyển khoản
          </button>
          <button
            type="button"
            aria-pressed={method === 'Debt'}
            onClick={() => {
              setMethod('Debt')
              if (!debtor) setPicking(true)
            }}
          >
            📒 Ghi sổ
          </button>
        </div>

        {/*
          Ai đang được ghi sổ, và họ đã nợ bao nhiêu.
          Con số nợ cũ phải nằm ngay đây chứ không nằm trong một màn hình khác: người bấm
          nút này là nhân viên đang đứng trước khách, và đó là giây duy nhất họ còn quyền
          nói "anh trả bớt rồi em ghi tiếp".
        */}
        {method === 'Debt' && (
          <button
            type="button"
            className={`debt-pick${overLimit ? ' is-over' : ''}`}
            onClick={() => setPicking(true)}
          >
            {debtor ? (
              <>
                <span className="debt-who">
                  <b>{debtor.name}</b>
                  <small>
                    {debtorOwes > 0 ? `Đang nợ ${money(debtorOwes)}đ` : 'Chưa nợ đồng nào'}
                    {debtor.debtLimit > 0 ? ` · trần ${money(debtor.debtLimit)}đ` : ''}
                  </small>
                </span>
                <span className="debt-after num">
                  {money(debtorOwes + total)}đ
                  <em>sau đơn này</em>
                </span>
              </>
            ) : (
              <span className="debt-who">
                <b>Chọn khách quen</b>
                <small>Ghi sổ thì phải biết ghi cho ai</small>
              </span>
            )}
          </button>
        )}

        {overLimit && (
          <p className="hint is-warn">
            Ghi thêm đơn này là {debtor?.name} vượt trần {money(debtor?.debtLimit ?? 0)}đ chủ quán
            đặt. Vẫn ghi được, nhưng nên hỏi chủ quán một tiếng.
          </p>
        )}

        {/*
          Khai thông tin người mua TRƯỚC khi thu tiền, không phải sau.
          Hoá đơn xuất rồi thì mã số thuế người mua không sửa được nữa — phải lập hoá đơn
          thay thế, việc mà không chủ quán nhỏ nào muốn dính vào.
        */}
        {eInvoiceOn && (
          <button type="button" className="row-btn" onClick={() => setAskBuyer(true)}>
            <span className="row-icon" aria-hidden="true">
              🧾
            </span>
            <span className="row-text">
              <b>{buyer ? (buyer.name ?? 'Khách lấy hoá đơn') : 'Khách lấy hoá đơn?'}</b>
              <small>
                {buyer?.taxCode
                  ? `MST ${buyer.taxCode}`
                  : buyer
                    ? 'Đã khai thông tin người mua'
                    : shop?.eInvoiceAutoIssue
                      ? 'Đơn nào cũng có hoá đơn — bấm để khai mã số thuế công ty'
                      : 'Bấm nếu khách cần hoá đơn'}
              </small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        )}

        </div>

        <div className="pay-side">
        {method === 'Cash' ? (
          <>
            <div className="chip-row chip-cash">
              {/*
                "Đúng tiền" là phím được bấm nhiều nhất trong cả hàng này, mà nó lại nằm
                lẫn giữa bốn con số trông y hệt. Tô chữ màu thương hiệu để mắt bắt được
                nó ngay, không phải đọc từng ô.
              */}
              {cashSuggestions(total).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`chip${v === total ? ' is-exact' : ''}`}
                  aria-pressed={given === v}
                  onClick={() => setGiven(v)}
                >
                  {v === total ? 'Đúng tiền' : money(v)}
                </button>
              ))}
            </div>
            <NumPad value={given} onChange={setGiven} />
          </>
        ) : method === 'Transfer' ? (
          <VietQr shop={shop} amount={total} code={order.code} />
        ) : (
          /*
            Ghi sổ thì khoang này trống — không có tiền để đếm, không có mã để quét.
            Thay vì để trống trơn, nhắc lại đúng một câu: món đưa cho khách ngay, tiền ghi
            vào sổ của người ấy. Nhân viên mới vào làm cần đọc câu này một lần.
          */
          <div className="debt-side">
            <div className="empty-icon" aria-hidden="true">
              📒
            </div>
            <p>
              {debtor
                ? `Đơn này ghi vào sổ của ${debtor.name}. Khách nhận món ngay, tiền thu sau.`
                : 'Chọn khách quen ở bên trái rồi mới ghi sổ được.'}
            </p>
          </div>
        )}
        </div>
        </div>
      </div>

      <div className="dock">
        <button type="button" className="btn btn-brand btn-lg grow" disabled={!canPay} onClick={pay}>
          {method === 'Debt'
            ? debtor
              ? `Ghi sổ ${money(total)}đ`
              : 'Chọn khách quen trước'
            : canPay
              ? `Xong · ${money(total)}đ`
              : `Còn thiếu ${money(Math.abs(change))}đ`}
        </button>
      </div>
    </div>
  )
}
